// utils/authErrors.js
// ============================================
// Which server answers END a session, and the one-shot notice the login page
// shows afterwards.
//
// Only an explicit answer from the auth middleware ends a session
// (user-service/src/middleware/auth.js → AUTH_ERROR_CODES). Everything else —
// a network error, a timeout, a 5xx, a 503 AUTH_UNAVAILABLE, or a plain 403
// from a feature/permission check — leaves the session alone.
// ============================================

export const SESSION_END_REASONS = Object.freeze({
  ENDED: "session_ended",
  INACTIVE: "account_inactive",
});

// 401 codes that mean the token can no longer identify anyone.
const SESSION_ENDING_401_CODES = new Set(["TOKEN_MISSING", "TOKEN_INVALID", "SESSION_USER_GONE"]);
const ACCOUNT_INACTIVE_CODE = "ACCOUNT_INACTIVE";

/**
 * Classify an auth-middleware rejection body.
 * @returns {null | "session_ended" | "account_inactive"}
 */
export const sessionEndReasonFromBody = (status, body) => {
  const code = body && typeof body === "object" ? body.code : null;
  if (!code) return null;
  if (status === 401 && SESSION_ENDING_401_CODES.has(code)) return SESSION_END_REASONS.ENDED;
  if (status === 403 && code === ACCOUNT_INACTIVE_CODE) return SESSION_END_REASONS.INACTIVE;
  return null;
};

/**
 * Read a Response (without consuming it for the caller) and classify it.
 * Only 401/403 bodies are read; every other status returns null at once.
 */
export const sessionEndReasonFromResponse = async (response) => {
  if (!response || (response.status !== 401 && response.status !== 403)) return null;
  try {
    const body = await response.clone().json();
    return sessionEndReasonFromBody(response.status, body);
  } catch (err) {
    return null;
  }
};

// ── Login-page notice ───────────────────────────────────────────────────────
// Same one-shot sessionStorage pattern as utils/postLoginRedirect.js. Written
// AFTER the session cleanup (so the cleanup can't erase it), read once by the
// login page, and ignored when stale.
const NOTICE_KEY = "authLogoutNotice";
const NOTICE_MAX_AGE_MS = 5 * 60 * 1000;

export function rememberLogoutNotice(reason) {
  if (!Object.values(SESSION_END_REASONS).includes(reason)) return;
  try {
    sessionStorage.setItem(NOTICE_KEY, JSON.stringify({ reason, at: Date.now() }));
  } catch (err) {
    /* storage unavailable — the login page simply shows no notice */
  }
}

export function consumeLogoutNotice() {
  try {
    const raw = sessionStorage.getItem(NOTICE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(NOTICE_KEY);
    const { reason, at } = JSON.parse(raw);
    if (!at || Date.now() - at > NOTICE_MAX_AGE_MS) return null;
    return Object.values(SESSION_END_REASONS).includes(reason) ? reason : null;
  } catch (err) {
    return null;
  }
}
